import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  RotateCcw,
  Plus,
  ChevronUp,
  Send,
  Printer,
  Search,
  X,
  Maximize,
  Minimize,
  Sun,
} from "feather-icons-react/build/IconComponents";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { OverlayTrigger } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { pmHue, pmInitials } from "../../components/master/modal/Product/pmUi";
import Selectbranch from "../../components/master/modal/Selectbranch";
import { deleteProduct } from "../../services/MasterApiServices";
import {
  fetchBrand,
  fetchCategory,
  fetchGroup,
  fetchProduct,
  fetchTax,
  fetchType,
  fetchUnit,
} from "../../redux/userSetting";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { setheadertoggle } from "../../redux/toggle";
import { renderTooltip } from "../../redux/helper";
import ProductBranchTransfer from "../../components/master/modal/Product/ProductBranchTransfer";
import ProductDetail from "../../components/master/modal/Product/ProductDetail";
import {
  BATCH,
  fmt,
  grouped,
  num,
  pBarcodes,
  pBrand,
  pCategory,
  pCode,
  pGroup,
  pStock,
  pType,
} from "../../components/master/modal/Product/productModel";
import LoadingUtils from "../../core/utils/LoadingUtils";
import {
  exportMasterExcel,
  exportMasterPDF,
  printMasterReport,
} from "../../core/utils/MasterReportUtils";
import { fetchBranchSettings } from "../../redux/settingbyBranch";
import { ProductPos } from "../../core/json/ProductPos";

const NO_FILTERS = { q: "", category: "", group: "", itemType: "", brand: "" };

const typing = () => {
  const a = document.activeElement;
  return !!a && (a.tagName === "INPUT" || a.tagName === "TEXTAREA" || a.tagName === "SELECT" || a.isContentEditable);
};

const Product = () => {
  const dispatch = useDispatch();
  const { togglehead } = useSelector((state) => state.headerToggle);
  const { branch } = useSelector((state) => state.branchlist);
  const { userId } = useSelector((state) => state.userauth);
  const { menuItems, specialpermission } = useSelector((state) => state.amc);
  const {
    productloading,
    productList,
    formattedTypeList,
    formattedCategoryList,
    categoryList,
    unitList,
    groupList,
    taxList,
    brandList,
  } = useSelector((state) => state.usersetting);

  const [permit, setpermit] = useState();
  const [branchpermit, setbranchpermit] = useState();

  // list state
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState(NO_FILTERS);
  const [sort, setSort] = useState({ key: "name", dir: 1 });
  const [shown, setShown] = useState(BATCH);

  // detail state: nothing open, a new product, or an existing one by guid
  const [open, setOpen] = useState(null); // null | { guid } | { guid: null }
  const [detailTick, setDetailTick] = useState(0);
  const [pendingCode, setPendingCode] = useState(null);
  const [isFull, setIsFull] = useState(!!document.fullscreenElement);
  const dirtyRef = useRef(false);

  const listRef = useRef(null);
  const sentinelRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    dispatch(fetchCategory(branch.guid));
    dispatch(fetchGroup(branch.guid));
    dispatch(fetchProduct(branch));
    dispatch(fetchBranchSettings(branch.guid));
  }, [branch]);

  useEffect(() => {
    dispatch(fetchType());
    dispatch(fetchUnit());
    dispatch(fetchTax());
    dispatch(fetchBrand());
    setbranchpermit(
      specialpermission?.userSpecialPermissionList?.find(
        (e) => e.specialPermission.name == "Product Branch Transfer"
      )
    );
    setpermit(menuItems?.menuPermissions?.find((e) => e.menu.name == "Product"));
  }, []);

  // ---------- lookups ----------
  const byGuid = (list) => Object.fromEntries((list || []).map((x) => [x.guid, x]));
  const catMap = useMemo(() => byGuid(categoryList), [categoryList]);
  const grpMap = useMemo(() => byGuid(groupList), [groupList]);
  const unitMap = useMemo(() => byGuid(unitList), [unitList]);
  const taxMap = useMemo(() => byGuid(taxList), [taxList]);

  // retail as the customer sees it: RetailRate is stored net of tax
  const shelfPrice = (p) => {
    const net = num(p?.retailRate);
    if (!p?.isSalesTaxIncludedInPrice) return net;
    return net * (1 + num(taxMap[p?.taxID ?? p?.tax?.guid]?.percentage) / 100);
  };

  const groupOptions = useMemo(() => {
    const catOf = (g) => g?.categoryID ?? g?.category?.guid ?? g?.categoryGuid;
    const tagged = (groupList || []).some((g) => catOf(g));
    const list = tagged && filters.category
      ? (groupList || []).filter((g) => catOf(g) === filters.category)
      : groupList || [];
    return [...list].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [groupList, filters.category]);

  // ---------- search, filter, sort ----------
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, q: searchInput })), 120);
    return () => clearTimeout(t);
  }, [searchInput]);

  const q = filters.q.trim().toLowerCase();
  const { matches, ms } = useMemo(() => {
    const t0 = performance.now();
    const hit = (p) => {
      if (!q) return true;
      if (String(p.name ?? "").toLowerCase().includes(q)) return true;
      if (pCode(p).toLowerCase().includes(q)) return true;
      if (String(p.shortName ?? "").toLowerCase().includes(q)) return true;
      return pBarcodes(p).some((b) => b.toLowerCase().includes(q));
    };
    const out = (productList || []).filter(
      (p) =>
        (!filters.category || pCategory(p) === filters.category) &&
        (!filters.group || pGroup(p) === filters.group) &&
        (!filters.itemType || pType(p) === filters.itemType) &&
        (!filters.brand || pBrand(p) === filters.brand) &&
        hit(p)
    );
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    const { key, dir } = sort;
    out.sort((a, b) => {
      if (key === "name") return collator.compare(String(a.name ?? ""), String(b.name ?? "")) * dir;
      if (key === "stock") return ((pStock(a) ?? -Infinity) - (pStock(b) ?? -Infinity)) * dir || 0;
      return (shelfPrice(a) - shelfPrice(b)) * dir;
    });
    return { matches: out, ms: Math.round((performance.now() - t0) * 10) / 10 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productList, filters, sort, taxMap]);

  // back to the first batch whenever the result set changes
  useEffect(() => {
    setShown(BATCH);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [filters, sort]);

  // load the next batch as the bottom of the list scrolls into view
  useEffect(() => {
    const root = listRef.current;
    const target = sentinelRef.current;
    if (!root || !target || typeof IntersectionObserver === "undefined") return undefined;
    if (shown >= matches.length) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setShown((n) => n + BATCH);
      },
      { root, rootMargin: "240px" }
    );
    io.observe(target);
    return () => io.disconnect();
  }, [shown, matches.length]);

  const setFilter = (key, value) =>
    setFilters((f) => ({ ...f, [key]: value, ...(key === "category" ? { group: "" } : {}) }));
  const anyFilter = !!(filters.category || filters.group || filters.itemType || filters.brand || searchInput);
  const clearAll = () => {
    setSearchInput("");
    setFilters(NO_FILTERS);
  };

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: key === "name" ? 1 : -1 }));
  const sortMark = (key) => (sort.key === key ? (sort.dir > 0 ? "↑" : "↓") : "");

  const mark = (text) => {
    const s = String(text ?? "");
    const i = q ? s.toLowerCase().indexOf(q) : -1;
    if (i < 0) return s;
    return (
      <>
        {s.slice(0, i)}
        <mark>{s.slice(i, i + q.length)}</mark>
        {s.slice(i + q.length)}
      </>
    );
  };

  // ---------- opening products ----------
  const openProduct = useMemo(
    () => (open?.guid ? (productList || []).find((p) => p.guid === open.guid) || null : null),
    [open, productList]
  );

  const guard = (label) =>
    !dirtyRef.current ||
    window.confirm(`Discard the unsaved changes${label ? ` to ${label}` : ""}?`);

  const selectRow = (p) => {
    if (!permit?.edit || !p) return;
    if (open?.guid === p.guid) return;
    if (!guard(openProduct?.name)) return;
    dirtyRef.current = false;
    setOpen({ guid: p.guid });
  };

  function handleAddProduct() {
    if (!formattedTypeList?.length) return toast.error("Please enter type before adding product");
    if (!formattedCategoryList?.length) return toast.error("Please enter categroy before adding product");
    if (!unitList?.length) return toast.error("Please enter unit before adding product");
    if (!groupList?.length) return toast.error("Please enter group before adding product");
    if (!guard()) return undefined;
    dirtyRef.current = false;
    setOpen({ guid: null });
    setDetailTick((t) => t + 1);
    return undefined;
  }

  const closeDetail = () => {
    if (!guard(openProduct?.name)) return;
    dirtyRef.current = false;
    setOpen(null);
  };

  // after a save: reload the list, then reopen the saved product fresh
  const onSaved = async (code, name) => {
    dirtyRef.current = false;
    await dispatch(fetchProduct(branch));
    setPendingCode({ code, name });
  };
  useEffect(() => {
    if (!pendingCode) return;
    const same = (a, b) => String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();
    const list = productList || [];
    const saved =
      list.find((p) => same(pCode(p), pendingCode.code)) ||
      (pendingCode.name ? list.find((p) => same(p.name, pendingCode.name)) : null);
    setPendingCode(null);
    if (saved) {
      setOpen({ guid: saved.guid });
      setDetailTick((t) => t + 1);
      setTimeout(() => {
        document.querySelector(`.pm-prow[data-guid="${saved.guid}"]`)?.scrollIntoView({ block: "nearest" });
      }, 50);
    } else if (open?.guid === null) {
      // Saved, but the reloaded list does not show it: do not leave the
      // just-saved draft open as "New" — a second Save would create it twice.
      setOpen(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productList, pendingCode]);

  // the product open on the right disappeared (deleted, or branch switched)
  useEffect(() => {
    if (open?.guid && Array.isArray(productList) && !productList.some((p) => p.guid === open.guid)) {
      dirtyRef.current = false;
      setOpen(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productList]);

  // ↑ / ↓ through the filtered list
  const step = (delta) => {
    if (!matches.length || !permit?.edit) return;
    const i = matches.findIndex((p) => p.guid === open?.guid);
    const j = i < 0 ? 0 : i + delta;
    if (j < 0 || j >= matches.length) return;
    if (j >= shown) setShown(Math.ceil((j + 1) / BATCH) * BATCH);
    selectRow(matches[j]);
    setTimeout(() => {
      document
        .querySelector(`.pm-prow[data-guid="${matches[j].guid}"]`)
        ?.scrollIntoView({ block: "nearest" });
    }, 0);
  };

  // ---------- delete ----------
  const MySwal = withReactContent(Swal);
  const handleDeleteClick = (obj) => {
    MySwal.fire({
      title: "Are you sure?",
      text: `${obj.name || "This product"} will be deleted. You won't be able to revert this!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      cancelButtonColor: "#ff0000",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        if (!obj?.guid) throw new Error("This product has not been saved yet");
        // Same body the old screen sent (it read PascalCase keys off the
        // camelCase row, so these went out as undefined and were dropped).
        // Filling them in — "" for a product with no brand, numeric
        // StockTypeID, etc. — can fail model binding on api/Product/Delete.
        const objproduct = new ProductPos();
        objproduct.id = obj.id;
        objproduct.Branch = branch;
        objproduct.guid = obj.guid;
        [
          "CategoryID", "SubGroupID", "ItemTypeID", "StockTypeID", "UnitID", "TaxID",
          "ManufactureID", "ReorderLevel", "MinQty", "MaxQty", "MinPrice",
          "IsSalesTaxIncludedInPrice", "IsPurchaseTaxIncludedInPrice",
        ].forEach((k) => {
          objproduct[k] = undefined;
        });
        objproduct.ModifiedDate = new Date();
        objproduct.IsMenuItem = true;
        objproduct.updatedUser = `{${userId?.id}}`;
        const response = await deleteProduct(objproduct);
        if (response && response.data !== false) {
          toast.success(`Product ${pCode(obj)} deleted`);
          dirtyRef.current = false;
          setOpen(null);
          dispatch(fetchProduct(branch));
        } else {
          MySwal.fire({ title: "Error!", text: "Something went wrong.", icon: "error" });
        }
      } catch (error) {
        MySwal.fire({
          title: "Error!",
          text: error?.response?.data?.Message || error?.message || "The product was not deleted.",
          icon: "error",
        });
      }
    });
  };

  // ---------- exports (unchanged columns) ----------
  const columns = [
    { title: "Code", dataIndex: "prodCode" },
    { title: "Name", dataIndex: "name" },
    { title: "Short Name", dataIndex: "shortName" },
    { title: "Description", dataIndex: "shortDescription" },
  ];
  const getActiveTabData = () => ({
    data: matches,
    columns,
    fieldMapping: Object.fromEntries(columns.map((c) => [c.title, c.dataIndex])),
    Rname: "Product",
  });

  // ---------- theme, full screen, shortcuts ----------
  const toggleTheme = () => {
    const dark = document.documentElement.getAttribute("data-layout-mode") === "dark_mode";
    const next = dark ? "light_mode" : "dark_mode";
    document.documentElement.setAttribute("data-layout-mode", next);
    try {
      localStorage.setItem("colorschema", next);
    } catch (e) {
      /* storage blocked — the switch still applies for this session */
    }
  };
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      const p = document.documentElement.requestFullscreen?.();
      if (p?.catch) p.catch(() => toast.error("Full screen was blocked — press F11 instead"));
    } else {
      document.exitFullscreen?.();
    }
  };
  useEffect(() => {
    const onFs = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const keyRef = useRef(null);
  keyRef.current = (e) => {
    if (document.querySelector(".swal2-container, .modal.show")) return;
    if (e.key === "F3") {
      e.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
    } else if (e.key === "F11") {
      e.preventDefault();
      toggleFullScreen();
    } else if ((e.key === "n" || e.key === "N") && !typing() && !e.ctrlKey && !e.metaKey && !e.altKey) {
      if (!permit?.add) return;
      e.preventDefault();
      handleAddProduct();
    } else if (
      (e.key === "ArrowDown" || e.key === "ArrowUp") &&
      (!typing() || document.activeElement === searchRef.current)
    ) {
      e.preventDefault();
      step(e.key === "ArrowDown" ? 1 : -1);
    }
  };
  useEffect(() => {
    const h = (e) => keyRef.current?.(e);
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, []);

  // ---------- render ----------
  const visible = matches.slice(0, shown);

  return (
    <div className="pm-screen">
      <LoadingUtils loading={productloading} />
      <div className="page-wrapper pm-wrapper">
        <div className="content pm-page">
          {/* ============ COMMAND BAR ============ */}
          <header className="pm-cmd">
            <div className="pm-doc">
              <h1>Product Master</h1>
              <span className="pm-count">{grouped(productList?.length || 0)} items</span>
            </div>
            <div className="pm-spacer" />
            <div className="pm-cmd-actions">
              <OverlayTrigger placement="top" overlay={renderTooltip("PDF")}>
                <Link className="pm-iconbtn" onClick={() => exportMasterPDF(getActiveTabData, "Product")}>
                  <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                  PDF
                </Link>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={renderTooltip("Excel")}>
                <Link className="pm-iconbtn" onClick={() => exportMasterExcel(getActiveTabData, "Product")}>
                  <ImageWithBasePath src="assets/img/icons/excel.svg" alt="img" />
                  Excel
                </Link>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={renderTooltip("Print")}>
                <Link className="pm-iconbtn" onClick={() => printMasterReport(getActiveTabData, "Product")}>
                  <Printer />
                  Print
                </Link>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={renderTooltip("Refresh")}>
                <Link className="pm-iconbtn" aria-label="Refresh" onClick={() => dispatch(fetchProduct(branch))}>
                  <RotateCcw />
                </Link>
              </OverlayTrigger>
              <span className="pm-sepbar" />
              <button
                type="button"
                className="pm-iconbtn"
                title="Switch light / dark"
                aria-label="Switch light or dark theme"
                onClick={toggleTheme}
              >
                <Sun />
              </button>
              <button
                type="button"
                className="pm-iconbtn"
                title="Full screen (F11)"
                aria-label="Full screen"
                onClick={toggleFullScreen}
              >
                {isFull ? <Minimize /> : <Maximize />}
              </button>
              <OverlayTrigger placement="top" overlay={renderTooltip("Collapse")}>
                <Link
                  aria-label="Collapse"
                  id="collapse-header"
                  className={"pm-iconbtn" + (togglehead ? " active" : "")}
                  onClick={() => dispatch(setheadertoggle(!togglehead))}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
              {branchpermit?.userRole.id !== 0 && (
                <>
                  <span className="pm-sepbar" />
                  <Link
                    to="#"
                    className="pm-btn-soft"
                    data-bs-target="#product-branch-trans"
                    data-bs-toggle="modal"
                  >
                    <Send />
                    Branch Transfer
                  </Link>
                </>
              )}
            </div>
          </header>

          <div className="pm-grid">
            {/* ============ LIST ============ */}
            <section className="pm-listpanel">
              <div className="pm-searchrow">
                <div className="pm-searchwrap">
                  <Search />
                  <input
                    ref={searchRef}
                    type="text"
                    autoComplete="off"
                    placeholder="Search name, code or barcode — any part"
                    className="pm-search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  {searchInput && (
                    <button
                      type="button"
                      className="pm-clearsearch"
                      aria-label="Clear search"
                      onClick={() => {
                        setSearchInput("");
                        searchRef.current?.focus();
                      }}
                    >
                      <X />
                    </button>
                  )}
                  <span className="pm-kbd pm-search-kbd">F3</span>
                </div>
                {permit?.add && (
                  <button type="button" className="pm-btn-new" onClick={handleAddProduct}>
                    <Plus />
                    New
                  </button>
                )}
              </div>

              <div className="pm-filters">
                <select
                  className={"pm-fsel" + (filters.category ? " on" : "")}
                  aria-label="Filter by category"
                  value={filters.category}
                  onChange={(e) => setFilter("category", e.target.value)}
                >
                  <option value="">All categories</option>
                  {(categoryList || []).map((c) => (
                    <option key={c.guid} value={c.guid}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <select
                  className={"pm-fsel" + (filters.group ? " on" : "")}
                  aria-label="Filter by group"
                  value={filters.group}
                  onChange={(e) => setFilter("group", e.target.value)}
                >
                  <option value="">All groups</option>
                  {groupOptions.map((g) => (
                    <option key={g.guid} value={g.guid}>
                      {g.name}
                    </option>
                  ))}
                </select>
                <select
                  className={"pm-fsel" + (filters.itemType ? " on" : "")}
                  aria-label="Filter by item type"
                  value={filters.itemType}
                  onChange={(e) => setFilter("itemType", e.target.value)}
                >
                  <option value="">All item types</option>
                  {(formattedTypeList || []).map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <select
                  className={"pm-fsel" + (filters.brand ? " on" : "")}
                  aria-label="Filter by brand"
                  value={filters.brand}
                  onChange={(e) => setFilter("brand", e.target.value)}
                >
                  <option value="">All brands</option>
                  {[...(brandList || [])]
                    .sort((a, b) => String(a.name).localeCompare(String(b.name)))
                    .map((b) => (
                      <option key={b.guid} value={b.guid}>
                        {b.name}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  className="pm-clearfilters"
                  onClick={clearAll}
                  disabled={!anyFilter}
                  title="Clear search and filters"
                  aria-label="Clear search and filters"
                >
                  <X />
                </button>
              </div>

              <div className="pm-listhead">
                <span />
                <span>
                  <button type="button" className="pm-sortbtn" onClick={() => toggleSort("name")}>
                    Product {sortMark("name")}
                  </button>
                </span>
                <span className="r">
                  <button type="button" className="pm-sortbtn" onClick={() => toggleSort("stock")}>
                    Stock {sortMark("stock")}
                  </button>
                </span>
                <span className="r">
                  <button type="button" className="pm-sortbtn" onClick={() => toggleSort("price")}>
                    Retail {sortMark("price")}
                  </button>
                </span>
              </div>

              <div className="pm-listwrap" ref={listRef}>
                {visible.map((p) => {
                  const stock = pStock(p);
                  const level =
                    stock === null ? "" : stock <= 0 ? " nil" : stock <= num(p.reorderLevel) ? " low" : "";
                  const bcs = pBarcodes(p).length;
                  const cat = catMap[pCategory(p)]?.name || p?.category?.name;
                  const grp = grpMap[pGroup(p)]?.name;
                  const inactive = p.isActive === false;
                  return (
                    <div
                      key={p.guid || p.id}
                      data-guid={p.guid}
                      role="button"
                      tabIndex={permit?.edit ? 0 : -1}
                      className={
                        "pm-prow" + (open?.guid === p.guid ? " sel" : "") + (permit?.edit ? "" : " ro")
                      }
                      onClick={() => selectRow(p)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          selectRow(p);
                        }
                      }}
                      title={p.shortDescription || ""}
                    >
                      <span className="pm-thumb" style={{ "--h": pmHue(pCategory(p) || p?.name) }}>
                        {pmInitials(p.name)}
                      </span>
                      <span className="pm-pmain">
                        <span className="pm-pname">
                          {inactive && <span className="pm-pdot" title="Inactive" />}
                          {mark(p.name)}
                        </span>
                        <span className="pm-psub">
                          <span className="pm-code">{mark(pCode(p))}</span>
                          {cat ? ` · ${cat}` : ""}
                          {grp ? ` › ${grp}` : ""}
                          {" · "}
                          <span className="pm-bcn">{bcs}</span> barcode{bcs === 1 ? "" : "s"}
                        </span>
                      </span>
                      <span className={"pm-pstock" + level}>
                        {stock === null ? "—" : grouped(stock)}
                        <i>{unitMap[p.unitID]?.name || ""}</i>
                      </span>
                      <span className="pm-pprice">
                        {fmt(shelfPrice(p))}
                        <i>{p.isSalesTaxIncludedInPrice ? "incl" : "excl"} tax</i>
                      </span>
                    </div>
                  );
                })}
                <div className="pm-sentinel" ref={sentinelRef} />
                {shown < matches.length && (
                  <div className="pm-loadmore">
                    <button type="button" onClick={() => setShown((n) => n + BATCH)}>
                      Load {BATCH} more
                    </button>
                  </div>
                )}
                {!matches.length && !productloading && (
                  <div className="pm-noresult">
                    <div className="big">Nothing matches</div>
                    <div>Try a shorter search, or clear the filters.</div>
                  </div>
                )}
              </div>

              <div className="pm-listfoot">
                <span>
                  Showing <b>{grouped(Math.min(shown, matches.length))}</b> of <b>{grouped(matches.length)}</b>
                </span>
                <span className="right">
                  {shown >= matches.length ? "all loaded" : `${BATCH} per batch · scroll for more`}
                </span>
              </div>
            </section>

            {/* ============ DETAIL ============ */}
            {open && (open.guid === null || openProduct) ? (
              <ProductDetail
                key={`${open.guid || "new"}-${detailTick}`}
                product={openProduct}
                productList={productList}
                permit={permit}
                onSaved={onSaved}
                onClose={closeDetail}
                onDelete={handleDeleteClick}
                onDirtyChange={(d) => {
                  dirtyRef.current = d;
                }}
              />
            ) : (
              <section className="pm-detail pm-dempty">
                <div className="in">
                  <svg
                    viewBox="0 0 48 48"
                    width="40"
                    height="40"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    aria-hidden="true"
                  >
                    <path d="M24 5 42 14v20L24 43 6 34V14z" />
                    <path d="M6 14l18 9 18-9M24 23v20" />
                  </svg>
                  <h2>Pick a product</h2>
                  <p>
                    Select a row on the left to edit it here
                    {permit?.add ? (
                      <>
                        , or press <span className="pm-kbd">N</span> to start a new one
                      </>
                    ) : null}
                    . The list stays put while you work.
                  </p>
                </div>
              </section>
            )}
          </div>

          <footer className="pm-status">
            <span className="grp">
              <span className="pm-kbd">F3</span>Search
            </span>
            {permit?.add && (
              <span className="grp">
                <span className="pm-kbd">N</span>New product
              </span>
            )}
            <span className="grp">
              <span className="pm-kbd">↑↓</span>Move through the list
            </span>
            <span className="grp">
              <span className="pm-kbd">F9</span>Save
            </span>
            <span className="grp">
              <span className="pm-kbd">Esc</span>Discard changes
            </span>
            <span className="sp" />
            <span className="grp">
              {matches.length} matched in {ms} ms · {grouped(productList?.length || 0)} in the master
            </span>
          </footer>
        </div>
        <Selectbranch />
        <ProductBranchTransfer />
      </div>
    </div>
  );
};

export default Product;
